import React from "react";
import styled, { keyframes } from "styled-components";
import { FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";

/**
 * Reusable confirmation / notice modal.
 *
 * - Confirm mode (default): title, message, Cancel + Confirm buttons.
 *   `danger` styles the confirm button red for destructive actions.
 * - Alert mode (`alertOnly`): a single OK button, used instead of window.alert().
 */
const ConfirmModal = ({
  open,
  title,
  message,
  items,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  alertOnly = false,
  loading = false,
  onConfirm,
  onClose,
}) => {
  if (!open) return null;

  return (
    <Overlay onClick={loading ? undefined : onClose}>
      <Box onClick={(e) => e.stopPropagation()}>
        <IconWrap $danger={danger}>
          {danger ? <FaExclamationTriangle /> : <FaInfoCircle />}
        </IconWrap>
        <h3>{title}</h3>
        {message && <p>{message}</p>}
        {items && items.length > 0 && (
          <ItemList>
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ItemList>
        )}
        <Actions>
          {!alertOnly && (
            <CancelBtn type="button" onClick={onClose} disabled={loading}>
              {cancelLabel}
            </CancelBtn>
          )}
          <ConfirmBtn
            type="button"
            $danger={danger}
            onClick={alertOnly ? onClose : onConfirm}
            disabled={loading}
          >
            {loading ? "Please wait…" : alertOnly ? "OK" : confirmLabel}
          </ConfirmBtn>
        </Actions>
      </Box>
    </Overlay>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(11, 30, 51, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
`;

const Box = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.75rem 1.5rem 1.5rem;
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  animation: ${fadeIn} 0.22s ease;

  h3 {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0b1e33;
    margin: 0 0 0.4rem;
  }

  p {
    font-size: 0.85rem;
    color: #718096;
    line-height: 1.5;
    margin: 0;
    word-break: break-word;
  }

  @media screen and (max-width: 480px) {
    padding: 1.25rem 1rem 1rem;
  }
`;

const ItemList = styled.ul`
  list-style: none;
  text-align: left;
  margin: 0.9rem 0 0;
  padding: 0.65rem 0.9rem;
  background: #f8f9fa;
  border-radius: 10px;
  max-height: 220px;
  overflow-y: auto;

  li {
    font-size: 0.82rem;
    color: #4a5568;
    padding: 0.3rem 0;
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;

    &:not(:last-child) {
      border-bottom: 1px dashed #e2e8f0;
    }

    &::before {
      content: "•";
      color: #d4af37;
      font-weight: 700;
      flex-shrink: 0;
    }
  }
`;

const IconWrap = styled.div`
  width: 52px;
  height: 52px;
  margin: 0 auto 0.9rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: ${(p) => (p.$danger ? "#e53e3e" : "#d4af37")};
  background: ${(p) =>
    p.$danger ? "rgba(229, 62, 62, 0.1)" : "rgba(212, 175, 55, 0.12)"};
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.4rem;

  @media screen and (max-width: 480px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const CancelBtn = styled.button`
  padding: 0.6rem 1.4rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: inherit;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #cbd5e0;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmBtn = styled.button`
  padding: 0.6rem 1.4rem;
  background: ${(p) => (p.$danger ? "#e53e3e" : "#d4af37")};
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: inherit;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default ConfirmModal;
